import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Bell, Sparkles, Wand2, Zap, Play, Pause, Timer } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { API, formatTime } from "@/utils/constants";

const AVATAR_PALETTE = ["#6366F1", "#EC4899", "#F59E0B", "#10B981", "#3B82F6", "#8B5CF6", "#EF4444", "#14B8A6", "#F97316", "#06B6D4", "#84CC16", "#D946EF"];

export const getAvatarColor = (key) => {
  const s = String(key || '');
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return AVATAR_PALETTE[h % AVATAR_PALETTE.length];
};

export const getInitials = (nameOrEmail) => {
  const s = String(nameOrEmail || '').trim();
  if (!s) return '?';
  const base = s.includes('@') ? s.split('@')[0] : s;
  const parts = base.split(/[\s._-]+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return base.slice(0, 2).toUpperCase();
};

export const getShortName = (nameOrEmail) => {
  const s = String(nameOrEmail || '').trim();
  if (!s) return '?';
  if (s.includes('@')) return getInitials(s);
  return s.split(' ')[0];
};

export const Avatar = ({ name, color, size = "sm" }) => {
  const initials = getInitials(name);
  const sizeClasses = { sm: "w-6 h-6 text-xs", md: "w-8 h-8 text-sm", lg: "w-10 h-10 text-base" };
  return <div className={`contact-avatar rounded-full ${sizeClasses[size]}`} style={{ backgroundColor: color || getAvatarColor(name) }} title={name}>{initials}</div>;
};

export const MentionText = ({ text }) => {
  if (!text) return null;
  const regex = /@\[([^\]]+)\]\(([^)]+)\)/g;
  const parts = [];
  let lastIndex = 0, match, key = 0;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) parts.push(text.slice(lastIndex, match.index));
    const initials = match[1].trim().split(/\s+/).map(w => w[0]).slice(0, 2).join("").toUpperCase();
    parts.push(
      <span key={`m${key++}`} title={match[1]} className="inline-flex items-center justify-center align-middle h-4.5 min-w-[18px] px-1 rounded-full bg-primary/15 text-primary text-[9px] font-bold mx-0.5" data-testid="mention-chip">{initials}</span>
    );
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) parts.push(text.slice(lastIndex));
  return <>{parts}</>;
};

export const MentionInput = ({ value, onChange, contacts, placeholder }) => {
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [filteredContacts, setFilteredContacts] = useState([]);
  const [cursorPosition, setCursorPosition] = useState(0);
  const inputRef = useRef(null);

  const handleChange = (e) => {
    const val = e.target.value;
    const pos = e.target.selectionStart;
    setCursorPosition(pos);
    onChange(val);

    const textBeforeCursor = val.substring(0, pos);
    const atMatch = textBeforeCursor.match(/@(\w*)$/);
    if (atMatch) {
      const query = atMatch[1].toLowerCase();
      const filtered = contacts.filter(c => c.name.toLowerCase().includes(query) || c.email.toLowerCase().includes(query));
      setFilteredContacts(filtered);
      setShowSuggestions(filtered.length > 0);
    } else {
      setShowSuggestions(false);
    }
  };

  const insertMention = (contact) => {
    const textBeforeCursor = value.substring(0, cursorPosition);
    const textAfterCursor = value.substring(cursorPosition);
    const atIndex = textBeforeCursor.lastIndexOf('@');
    const newText = textBeforeCursor.substring(0, atIndex) + `@${contact.name} ` + textAfterCursor;
    onChange(newText);
    setShowSuggestions(false);
  };

  return (
    <div className="relative">
      <Input ref={inputRef} value={value} onChange={handleChange} placeholder={placeholder} data-testid="mention-input" />
      {showSuggestions && (
        <div className="absolute bottom-full mb-1 left-0 w-full bg-popover border rounded-lg shadow-lg z-50 max-h-32 overflow-y-auto">
          {filteredContacts.map(c => (
            <button key={c.id} className="w-full px-3 py-2 text-left hover:bg-muted flex items-center gap-2 text-sm" onClick={() => insertMention(c)}>
              <Avatar name={c.name} color={c.avatar_color} size="sm" />
              <span>{c.name}</span>
              <span className="text-muted-foreground text-xs">{c.email}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export const MentionTextarea = ({ value, onChange, contacts, placeholder, rows = 3 }) => {
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [filteredContacts, setFilteredContacts] = useState([]);
  const [cursorPosition, setCursorPosition] = useState(0);
  const textareaRef = useRef(null);

  const handleChange = (e) => {
    const val = e.target.value;
    const pos = e.target.selectionStart;
    setCursorPosition(pos);
    onChange(val);
    const textBeforeCursor = val.substring(0, pos);
    const atMatch = textBeforeCursor.match(/@(\w*)$/);
    if (atMatch) {
      const query = atMatch[1].toLowerCase();
      const filtered = (contacts || []).filter(c => c.name?.toLowerCase().includes(query) || c.email?.toLowerCase().includes(query));
      setFilteredContacts(filtered);
      setShowSuggestions(filtered.length > 0);
    } else {
      setShowSuggestions(false);
    }
  };

  const insertMention = (contact) => {
    const textBeforeCursor = value.substring(0, cursorPosition);
    const textAfterCursor = value.substring(cursorPosition);
    const atIndex = textBeforeCursor.lastIndexOf('@');
    const newText = textBeforeCursor.substring(0, atIndex) + `@${contact.name} ` + textAfterCursor;
    onChange(newText);
    setShowSuggestions(false);
    textareaRef.current?.focus();
  };

  return (
    <div className="relative">
      <textarea ref={textareaRef} value={value} onChange={handleChange} placeholder={placeholder} rows={rows} className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 resize-y" data-testid="mention-textarea" />
      {showSuggestions && (
        <div className="absolute top-full mt-1 left-0 w-full bg-popover border rounded-lg shadow-lg z-50 max-h-32 overflow-y-auto">
          {filteredContacts.map(c => (
            <button key={c.id} className="w-full px-3 py-2 text-left hover:bg-muted flex items-center gap-2 text-sm" onClick={() => insertMention(c)}>
              <Avatar name={c.name} color={c.avatar_color} size="sm" />
              <span>{c.name}</span>
              <span className="text-muted-foreground text-xs">{c.email}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};


export const TimeTracker = ({ task, onStart, onStop }) => {
  const [elapsed, setElapsed] = useState(0);
  const activeEntry = task?.time_entries?.find(e => !e.ended_at);

  useEffect(() => {
    if (!activeEntry) { setElapsed(0); return; }
    const started = new Date(activeEntry.started_at);
    const interval = setInterval(() => {
      setElapsed(Math.floor((Date.now() - started.getTime()) / 60000));
    }, 1000);
    return () => clearInterval(interval);
  }, [activeEntry]);

  return (
    <div className="flex items-center gap-2">
      {activeEntry ? (
        <>
          <div className="flex items-center gap-1 text-sm font-mono text-destructive animate-pulse"><Timer className="h-4 w-4" />{formatTime(elapsed)}</div>
          <Button size="sm" variant="destructive" onClick={() => onStop(task.id)} data-testid="stop-timer-btn"><Pause className="h-3 w-3 mr-1" />Stop</Button>
        </>
      ) : (
        <>
          {task?.total_time_minutes > 0 && <span className="text-xs text-muted-foreground">{formatTime(task.total_time_minutes)}</span>}
          <Button size="sm" variant="outline" onClick={() => onStart(task.id)} data-testid="start-timer-btn"><Play className="h-3 w-3 mr-1" />Start</Button>
        </>
      )}
    </div>
  );
};

export const NotificationsPanel = ({ notifications, onMarkRead, onOpenTask }) => (
  <Popover>
    <PopoverTrigger asChild>
      <Button variant="ghost" size="icon" className="relative" data-testid="notifications-btn">
        <Bell className="h-5 w-5" />
        {notifications.filter(n => !n.read).length > 0 && <span className="absolute -top-1 -right-1 bg-destructive text-destructive-foreground text-xs rounded-full w-5 h-5 flex items-center justify-center">{notifications.filter(n => !n.read).length}</span>}
      </Button>
    </PopoverTrigger>
    <PopoverContent className="w-80 p-0" align="end">
      <div className="p-3 border-b"><h4 className="font-semibold text-sm">Notifications</h4></div>
      <ScrollArea className="max-h-64">
        {notifications.length === 0 ? <div className="p-4 text-center text-sm text-muted-foreground">No notifications</div> :
          notifications.map(n => (
            <div key={n.id} className={`p-3 border-b hover:bg-muted/50 cursor-pointer ${n.read ? 'opacity-60' : ''}`}
              data-testid={`notification-item-${n.id}`}
              onClick={() => { if (!n.read) onMarkRead(n.id); if (n.task_id && onOpenTask) onOpenTask(n.task_id); }}>
              <p className="text-sm">{n.message}</p>
              <div className="flex items-center gap-2 mt-1">
                {!n.read && <Badge variant="secondary" className="text-xs">New</Badge>}
                {n.task_id && <span className="text-[10px] text-primary font-medium">Click to open task →</span>}
              </div>
            </div>
          ))}
      </ScrollArea>
    </PopoverContent>
  </Popover>
);

export const AIQuickAdd = ({ onTaskCreated, isPro }) => {
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!input.trim()) return;
    setLoading(true);
    try {
      const r = await axios.post(`${API}/ai/parse-task`, { text: input });
      onTaskCreated(r.data);
      setInput("");
      toast.success("Task parsed!");
    } catch (e) {
      if (e.response?.status === 403) toast.error("Pro feature");
      else toast.error("Failed");
    }
    setLoading(false);
  };

  if (!isPro) return null;
  return (
    <div className="flex gap-2 items-center" data-testid="ai-quick-add">
      <div className="relative flex-1">
        <Sparkles className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-amber-500" />
        <Input value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleSubmit()} placeholder="AI Quick Add: 'Call John tomorrow at 3pm'" className="pl-9" data-testid="ai-quick-add-input" />
      </div>
      <Button onClick={handleSubmit} disabled={loading || !input.trim()} size="sm" data-testid="ai-quick-add-btn">
        {loading ? <div className="animate-spin h-4 w-4 border-2 border-current border-t-transparent rounded-full" /> : <><Wand2 className="h-4 w-4 mr-1" />Parse</>}
      </Button>
    </div>
  );
};
